import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Loader2, Building2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { businessTypes, cuisineTypes, priceRanges } from "../../lib/utils";

export function RestaurantProfileForm({ currentRestaurant, onSaved }) {
  const [formData, setFormData] = useState({
    name: "",
    business_type: "",
    cuisine_type: "",
    price_range: "medium",
    target_audience: "",
    address: "",
    phone: "",
    email: "",
    description: "",
    closed_days: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentRestaurant) return;
    setFormData({
      name: currentRestaurant.name || "",
      business_type: currentRestaurant.business_type || "",
      cuisine_type: currentRestaurant.cuisine_type || "",
      price_range: currentRestaurant.price_range || "medium",
      target_audience: currentRestaurant.target_audience || "",
      address: currentRestaurant.address || "",
      phone: currentRestaurant.phone || "",
      email: currentRestaurant.email || "",
      description: currentRestaurant.description || "",
      closed_days: currentRestaurant.closed_days || [],
    });
  }, [currentRestaurant]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    setSaving(true);
    try {
      await api.updateRestaurant(currentRestaurant.restaurant_id, formData);
      onSaved?.();
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error(error.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave}>
      <Card className="border-none shadow-soft mb-6">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Información básica
          </CardTitle>
          <CardDescription>Datos principales de tu establecimiento</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Nombre del negocio *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nombre de tu restaurante"
              data-testid="settings-name-input"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo de negocio</Label>
              <Select
                value={formData.business_type}
                onValueChange={(v) => setFormData({ ...formData, business_type: v })}
              >
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {businessTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tipo de cocina</Label>
              {(() => {
                const knownVals = cuisineTypes.map(t => t.value).filter(v => v !== 'other');
                const isPreset = knownVals.includes(formData.cuisine_type);
                const selectVal = isPreset ? formData.cuisine_type : 'other';
                const showInput = !isPreset;
                const inputVal = formData.cuisine_type === 'other' ? '' : (isPreset ? '' : formData.cuisine_type);
                return (
                  <>
                    <Select
                      value={selectVal}
                      onValueChange={(v) => setFormData({ ...formData, cuisine_type: v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        {cuisineTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {showInput && (
                      <Input
                        placeholder="Especifica el tipo de cocina..."
                        value={inputVal}
                        onChange={(e) => setFormData({ ...formData, cuisine_type: e.target.value || 'other' })}
                        data-testid="cuisine-type-custom-input"
                        autoFocus
                      />
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rango de precios</Label>
              <Select
                value={formData.price_range}
                onValueChange={(v) => setFormData({ ...formData, price_range: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {priceRanges.map((range) => (
                    <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Público objetivo</Label>
              <Input
                value={formData.target_audience}
                onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                placeholder="Ej: Familias, jóvenes..."
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe tu negocio..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-soft mb-6">
        <CardHeader>
          <CardTitle className="font-heading">Contacto</CardTitle>
          <CardDescription>Información de contacto y ubicación</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Dirección</Label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Calle, número, ciudad"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+34 600 000 000"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="contacto@turestaurante.com"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-soft mb-6">
        <CardHeader>
          <CardTitle className="font-heading">Días sin actividad</CardTitle>
          <CardDescription>
            Los días marcados se excluyen de las estadísticas y análisis de ventas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2" data-testid="closed-days-selector">
            {[
              { idx: 0, short: "L", long: "Lunes" },
              { idx: 1, short: "M", long: "Martes" },
              { idx: 2, short: "X", long: "Miércoles" },
              { idx: 3, short: "J", long: "Jueves" },
              { idx: 4, short: "V", long: "Viernes" },
              { idx: 5, short: "S", long: "Sábado" },
              { idx: 6, short: "D", long: "Domingo" },
            ].map(({ idx, short, long }) => {
              const isClosed = (formData.closed_days || []).includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  data-testid={`closed-day-${idx}`}
                  title={isClosed ? `${long} — cerrado` : `${long} — abierto`}
                  onClick={() => {
                    const current = formData.closed_days || [];
                    setFormData({
                      ...formData,
                      closed_days: isClosed
                        ? current.filter(d => d !== idx)
                        : [...current, idx],
                    });
                  }}
                  className={`w-10 h-10 rounded-full text-sm font-semibold border-2 transition-all select-none
                    ${isClosed
                      ? "bg-destructive/10 border-destructive text-destructive line-through opacity-80"
                      : "bg-background border-border text-foreground hover:border-primary"
                    }`}
                >
                  {short}
                </button>
              );
            })}
          </div>
          {(formData.closed_days || []).length > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              Cerrado: {(formData.closed_days || [])
                .sort((a, b) => a - b)
                .map(d => ["Lunes","Martes","Miércoles","Jueves","Viernes","Sábado","Domingo"][d])
                .join(", ")}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={saving}
          className="rounded-full px-8"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" />Guardar cambios</>
          )}
        </Button>
      </div>
    </form>
  );
}
